import { Button } from '@awj/ui';

import { Icon } from './Icon.tsx';
import { NavIcon } from './NavIcon.tsx';
import type { NavIconName } from './navigation.ts';
import { useT } from '~/i18n/index.tsx';

/**
 * A form that gets its own screen instead of a dialog over the list.
 *
 * A modal was the right shape for a two-field edit and the wrong one for everything else: 560px
 * of column for a form with fourteen controls in it, with the list still behind it doing nothing
 * except making the sheet feel like a lid over the work.
 *
 * What the page adds beyond width:
 *
 *   A BACK affordance, and a real one — the caller drives it with a route, so the browser's own
 *   back button does the same thing. A dialog has only an X, and the browser's back button under
 *   a dialog leaves the page entirely.
 *
 *   An ADDRESS. "The category you are editing" becomes a URL that can be linked, bookmarked and
 *   reloaded. Under a dialog, a refresh loses the form and lands on the list.
 *
 *   Room to lay fields out in more than one column, which is what actually fixes "cramped" —
 *   880px is wide enough for two columns of real fields and narrow enough that a line of help
 *   text is still a comfortable measure.
 *
 * The footer is sticky, so Save is reachable from anywhere in a long form without scrolling to
 * the end of it.
 *
 * Ported from the reference panel's `FormPage`, minus its stepper: none of the forms here is long
 * enough to be worth breaking into steps, and an unused wizard is a wizard that rots.
 */
export function FormPage({
  title,
  subtitle,
  icon,
  onBack,
  onSubmit,
  submitLabel,
  submitting = false,
  submitTone = 'primary',
  status,
  aside,
  children,
}: {
  title: string;
  subtitle?: string;
  icon?: NavIconName;
  onBack: () => void;
  onSubmit: (event: React.FormEvent) => void;
  submitLabel: string;
  submitting?: boolean;
  submitTone?: 'primary' | 'danger';
  /** A word beside Save about what it will do — "unsaved changes" — where the eye already is. */
  status?: string;
  aside?: React.ReactNode;
  children: React.ReactNode;
}) {
  const t = useT();

  return (
    <form className="formpage" onSubmit={onSubmit} noValidate>
      <header className="formpage__head">
        <div className="formpage__lead">
          {/*
            Back rather than a close X: this is a place you navigated TO, and the word for leaving
            it is the same one the browser uses.
          */}
          <button type="button" className="formpage__back" onClick={onBack}>
            <Icon name="back" className="rtl-flip" size={18} strokeWidth={2} />
            {/*
              The word, not just the arrow. The reference hides it, and on a panel where every
              form is a page that is a fair trade — you learn the glyph once. Here the forms have
              only just stopped being dialogs, and the control that replaces a familiar X should
              say what it does rather than ask to be recognised.
            */}
            <span className="formpage__back-label">{t('common.back')}</span>
          </button>

          {icon ? (
            <span className="formpage__icon" aria-hidden="true">
              <NavIcon name={icon} />
            </span>
          ) : null}

          <div className="formpage__titles">
            <h1 className="formpage__title">
              <bdi>{title}</bdi>
            </h1>
            {subtitle ? (
              <p className="formpage__subtitle">
                <bdi>{subtitle}</bdi>
              </p>
            ) : null}
          </div>
        </div>
      </header>

      <div className="formpage__body">
        <div className="formpage__col">{children}</div>
        {/* A form with something to SAY beside its fields — a preview, a summary — puts it here. */}
        {aside ? <div className="formpage__aside">{aside}</div> : null}
      </div>

      <footer className="formpage__foot">
        <div className="formpage__foot-inner">
          <Button type="button" variant="ghost" onClick={onBack}>
            {t('common.cancel')}
          </Button>

          <div className="formpage__moves">
            {status ? (
              <span className="formpage__status" role="status">
                {status}
              </span>
            ) : null}
            <Button type="submit" variant={submitTone} loading={submitting}>
              {submitLabel}
            </Button>
          </div>
        </div>
      </footer>
    </form>
  );
}
